import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';

/**
 * El logo de Aturno, haciéndose el tímido detrás de la caja de cierre.
 *
 * Vive escondido atrás de la tarjeta naranja. Cada tanto se asoma por un
 * costado, se queda un instante mirando y se mete de golpe. Después reaparece
 * por el otro lado. Es lo último de la landing: quien llegó hasta acá ya leyó
 * todo, así que el lugar admite un gesto que no explica nada y sólo está para
 * causar gracia.
 *
 * Quién lo tapa: LA TARJETA. Es lo único que hay que entender de acá.
 * El logo está detrás (`z-0` contra el `z-10` de ella) y en reposo queda
 * completamente dentro del ancho del naranja, o sea invisible. Para asomarse se
 * corre hacia afuera y lo que pasa del borde es lo único que se ve; el resto
 * sigue tapado. No hay recortes ni máscaras: el escondite es la caja, y por eso
 * se lee que está escondido AHÍ y no flotando en cualquier lado.
 *
 * La primera versión salía por abajo y con una franja propia con
 * `overflow: hidden`. Entre el borde naranja y esa franja quedaba el aire del
 * padding, así que el logo aparecía suelto en el gris, despegado de la caja: se
 * veía una manchita perdida en vez de alguien espiando.
 *
 * Los tiempos hacen la actuación: sale despacio (`easeOut`, frena al asomar),
 * se queda quieto un momento y se mete rápido (`easeIn`, acelera al huir). Si
 * las dos mitades duraran lo mismo sería un péndulo; lo que lo vuelve tímido es
 * que la huida sea más corta que la salida.
 *
 * El cruce de un lado al otro pasa mientras está tapado, así que no se lo ve
 * viajar: desaparece de un costado y reaparece por el otro. Por eso el ciclo son
 * DOS asomadas en una sola secuencia y no una animación que se repita igual.
 *
 * Dos frenos, los dos de la casa:
 *
 * - `prefers-reduced-motion` lo saca por completo. Un logo tímido que no se
 *   mueve es un logo cortado por la mitad, o sea que parece un error; acá lo
 *   correcto no es quedarse quieto sino no estar.
 * - Sólo se mueve mientras se lo ve. Es un bucle infinito: dejarlo corriendo al
 *   fondo de una página de la que ya te fuiste es gastar batería para nada.
 *
 * No confundir con `Aparecer`, que es la animación de ENTRADA de los bloques y
 * está deliberadamente desactivada. Esta arranca al entrar en pantalla y es
 * contenido, de la misma familia que ContadorAnimado o SimuladorWhatsApp.
 */

/** Lado del logo, en píxeles. */
const LOGO = 88;

/** Primer paso: sólo una parte, espiando. */
const ASOMA = 38;

export default function LogoTimido() {
  const caja = useRef(null);
  const enPantalla = useInView(caja, { amount: 'some' });
  const menosMovimiento = useReducedMotion();
  const [medidas, setMedidas] = useState({ ancho: 0, espacio: 0 });

  /* Se miden dos cosas y las dos hacen falta.

     `ancho` es el de la tarjeta: dice dónde está su borde derecho, que es el
     escondite del segundo asomo. Fijarlo en píxeles no sirve porque la tarjeta
     mide 1280 en una pantalla grande y 312 en un teléfono.

     `espacio` es cuánto hay entre la tarjeta y el borde de la pantalla, y es lo
     que limita hasta dónde puede salir. Sin esta cuenta, en una pantalla donde
     la tarjeta casi toca los bordes el logo saldría más allá del viewport: la
     página entera quedaría con scroll horizontal por una decoración. */
  useEffect(() => {
    const elemento = caja.current;
    if (!elemento) return undefined;

    const medir = () => {
      const marco = elemento.getBoundingClientRect();
      setMedidas({
        ancho: elemento.clientWidth,
        espacio: Math.min(marco.left, window.innerWidth - marco.right),
      });
    };
    medir();

    const observador = new ResizeObserver(medir);
    observador.observe(elemento);
    window.addEventListener('resize', medir);
    return () => {
      observador.disconnect();
      window.removeEventListener('resize', medir);
    };
  }, []);

  // Los hooks van todos antes de cualquier `return`, por eso este corte está acá
  // abajo y no arriba de todo.
  if (menosMovimiento) return null;

  const { ancho, espacio } = medidas;

  /* Hasta dónde puede salir: el logo entero si hay lugar, y si no, lo que
     entre. Los 8px son para no rozar el borde de la pantalla. */
  const SALIDA = Math.max(ASOMA, Math.min(LOGO, espacio - 8));

  // Posiciones horizontales, medidas desde el borde izquierdo de la tarjeta.
  const ESCONDIDO_IZQ = 0; // pegado al borde izquierdo, por dentro: tapado
  const ASOMADO_IZQ = -ASOMA; // espía: se ve una parte
  const AFUERA_IZQ = -SALIDA; // salió: se lo ve entero
  const ESCONDIDO_DER = Math.max(0, ancho - LOGO); // pegado al borde derecho, por dentro
  const ASOMADO_DER = ESCONDIDO_DER + ASOMA;
  const AFUERA_DER = ESCONDIDO_DER + SALIDA;

  return (
    /* `inset-0` lo hace del tamaño exacto de la tarjeta, que es contra lo que se
       miden los bordes. `z-0` contra el `z-10` de ella es lo que lo manda
       atrás. */
    <div
      ref={caja}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 select-none"
    >
      <motion.img
        src="/logo-aturno.png"
        alt=""
        className="absolute left-0 top-1/2 h-[88px] w-[88px] drop-shadow-xl"
        style={{ marginTop: -LOGO / 2 }}
        initial={{ x: ESCONDIDO_IZQ, rotate: 0 }}
        animate={
          enPantalla
            ? {
                x: [
                  ESCONDIDO_IZQ, // tapado
                  ASOMADO_IZQ, //   asoma
                  ASOMADO_IZQ, //   espera, mirando: ¿salgo o no salgo?
                  AFUERA_IZQ, //    se decide y sale entero
                  AFUERA_IZQ, //    afuera... y se da cuenta
                  ESCONDIDO_IZQ, // ¡se desespera y se esconde!
                  ESCONDIDO_DER, // cruza tapado
                  ASOMADO_DER, //   asoma
                  ASOMADO_DER, //   espera
                  AFUERA_DER, //    sale entero
                  AFUERA_DER, //    afuera
                  ESCONDIDO_DER, // ¡se esconde!
                  ESCONDIDO_IZQ, // vuelve tapado
                ],
                // Se inclina apenas al asomar y más al salir, como quien primero
                // saca la cabeza y después el cuerpo. Se endereza de golpe al
                // meterse: el enderezarse es parte del susto.
                rotate: [0, -8, -8, -12, -12, 0, 0, 8, 8, 12, 12, 0, 0],
              }
            : { x: ESCONDIDO_IZQ, rotate: 0 }
        }
        transition={
          enPantalla
            ? {
                duration: 9,
                /* Los tiempos son la actuación. La que manda es LA ESPERA: se
                   asoma rápido y después se queda quieto un rato, mirando, sin
                   decidirse. Sin esa pausa, asomarse y salir se leían como un
                   solo movimiento continuo y no había timidez por ningún lado,
                   sólo un logo apareciendo despacio.

                   La otra mitad del chiste es el final: apenas termina de salir
                   se da cuenta de que está expuesto y se mete de golpe. Afuera
                   dura 0,4 s y la huida 0,3 s, contra 1 s de duda. Cuesta
                   decidirse y no cuesta nada arrepentirse.

                   El segundo que se recortó del ciclo salió del CRUCE, que pasó
                   de 1,6 s a 1,1 s por lado. Es el único tramo donde el logo
                   está tapado y no se ve nada: acortar ahí es sacar tiempo
                   muerto y no recortar la actuación, que quedó intacta.

                       0     → 0.056  asoma           0,5 s
                       0.056 → 0.167  ESPERA          1,0 s   ← la duda
                       0.167 → 0.300  sale entero     1,2 s
                       0.300 → 0.344  afuera          0,4 s   ← se da cuenta
                       0.344 → 0.378  ¡se esconde!    0,3 s   ← el susto
                       0.378 → 0.5    cruza tapado    1,1 s   ← el recorte    */
                times: [
                  0, 0.056, 0.167, 0.3, 0.344, 0.378,
                  0.5, 0.556, 0.667, 0.8, 0.844, 0.878, 1,
                ],
                /* Una curva por tramo, doce en total.
                   `easeOut` al asomar: saca la cabeza y frena en seco.
                   `linear` en la espera y en los cruces, que no se ven mover.
                   `easeInOut` al salir: arranca dudando y se acomoda afuera.
                   `easeIn` al esconderse: acelera hasta desaparecer. */
                ease: [
                  'easeOut', 'linear', 'easeInOut', 'linear', 'easeIn', 'linear',
                  'easeOut', 'linear', 'easeInOut', 'linear', 'easeIn', 'linear',
                ],
                repeat: Infinity,
              }
            : { duration: 0.3 }
        }
      />
    </div>
  );
}
